import { useForm } from "@tanstack/react-form";
import { createFileRoute, redirect, useRouter, useSearch } from "@tanstack/react-router";
import { Info, OctagonAlert } from "lucide-react";
import { useEffect } from "react";
import { FieldInfo } from "@/components/form";
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@/components/ui";
import { useNewPasswordMutation } from "@/features/auth/hooks/use-new-password-mutation";
import { newPasswordSchema, tokenSchema } from "@/features/auth/schema/new-password.schema";
import { isString } from "@/utils/type-guard";

export const Route = createFileRoute("/auth/new-password")({
  validateSearch: tokenSchema,
  beforeLoad: async ({ search }) => {
    if (!search.token || !isString(search.token)) {
      throw redirect({ to: "/auth/login" });
    }
  },
  component: NewPassword,
});

const defaultValues = {
  newPassword: "",
  confirmPassword: "",
};

function NewPassword() {
  const { mutate: setPassword, error, isPending } = useNewPasswordMutation();
  const { token } = useSearch({ from: "/auth/new-password" });
  const router = useRouter();

  // 如果 token 不存在，則重新導向登入頁
  useEffect(() => {
    if (!token) router.navigate({ to: "/auth/login" });
  }, [token, router]);

  const form = useForm({
    defaultValues,
    validators: {
      onSubmit: newPasswordSchema,
    },
    onSubmit: ({ value }) => {
      setPassword({ ...value, token });
    },
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  return (
    <Card className="w-full max-w-[95vw] sm:max-w-lg border-0 shadow-lg">
      <CardHeader className="space-y-1">
        <CardTitle className="flex items-center justify-center text-2xl font-semibold">
          設定新密碼
        </CardTitle>
        <Alert>
          <Info />
          <AlertDescription>請為您的帳號輸入您的新密碼，密碼長度至少為 8 個字元。</AlertDescription>
        </Alert>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <OctagonAlert />
              <AlertDescription>設定密碼時發生錯誤，請再試一次。</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <form.Field
              name="newPassword"
              children={(field) => (
                <>
                  <Label htmlFor={field.name}>新密碼</Label>
                  <Input
                    type="password"
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    placeholder="請輸入新密碼"
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <FieldInfo field={field} />
                </>
              )}
            />
          </div>

          <div className="space-y-2">
            <form.Field
              name="confirmPassword"
              children={(field) => (
                <>
                  <Label htmlFor={field.name}>確認新密碼</Label>
                  <Input
                    type="password"
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    placeholder="請再次輸入新密碼"
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <FieldInfo field={field} />
                </>
              )}
            />
          </div>
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                className="w-full"
                disabled={!canSubmit || isSubmitting || isPending}
              >
                {isSubmitting ? "設定中..." : "設定密碼"}
              </Button>
            )}
          />
        </form>
      </CardContent>
    </Card>
  );
}
